/* eslint-disable i18next/no-literal-string */
/*
    This the composition preview Container.
    This is the drop target for ArtItems.
*/

import React, { Component } from 'react'
import { func, string, number, bool } from 'prop-types'
import { connect } from 'react-redux'
import { compositionType, debugSettingsType, previewSettingsType, dimensionsType } from 'models/propTypes'
import DeviceOrientation, { Orientation } from 'react-screen-orientation'
import classNames from 'classnames'
import fscreen from 'fscreen'

import { CONTEXT_DEFAULT } from 'constants/contexts'
import getMaxWindowSize from 'utils/getMaxWindowSize'

import { getSelectedComposition, getSurfacesInteractive } from 'models/compositions/selectors'
import { getFetchState } from 'models/scenes/selectors'
import { getIsLoggedIn } from 'models/user/selectors'
import {
    getIsCurrentDragValid,
    getPreviewSettings,
    getIsMagicWindowLoading,
    getWallDataDimensions,
} from 'models/preview/selectors'
import { getDebugSettings } from 'models/admin/selectors'
import { getCanvasWidth, getMaxTextureSize } from 'models/location/selectors'
import { getShowMissingWallsModal, getOrientation } from 'models/ui/selectors'
// TODO move this to the renderer selector
import { getRenderer } from 'models/refStore/selectors'
import {
    selectCompositionRenderItem,
    stackableSurfaceClicked,
    updateCompositionSaveData,
    removeRenderItems as removeRenderItemsAction,
    undoRemoveRenderItems as undoRemoveRenderItemsAction,
    clickPlaceStackableOnFloor,
    updateHiddenSurfaces,
} from 'models/compositions/actions'
import {
    newRenderer,
    destroyRenderer,
    rendererReady,
    firstRender,
    webglContextLost,
    webglContextNotCreated,
    webglContextRestored,
    initializationFailure,
} from 'models/renderer/actions'
import { wallDataReady, hidePlaceableSurfaces } from 'models/preview/actions'
import {
    showMissingWallsModal as showMissingWallsModalAction,
    setMobileOrientation as setMobileOrientationAction,
} from 'models/ui/actions'
import Preview from 'components/Preview'
import { withTranslation } from 'react-i18next'
import Icon from 'components/uiElements/Icon'

import SugarcubeContext from 'apps/SugarcubeContext'
import { getHasRendered } from 'models/renderer/selectors'
import { ORIENTATION_LANDSCAPE, ORIENTATION_PORTRAIT } from 'models/ui/constants'

const supportedOrientation = ORIENTATION_LANDSCAPE

class PreviewContainer extends Component {
    constructor(props) {
        super(props)
        this.onUpdateCompositionSaveData = this.onUpdateCompositionSaveData.bind(this)
        this.onOrientationChange = this.onOrientationChange.bind(this)
        this.onScroll = this.onScroll.bind(this)
        this.onScrollFinished = this.onScrollFinished.bind(this)
        this.onFullscreenChange = this.onFullscreenChange.bind(this)
        this.doUpdateHiddenSurfaces = this.doUpdateHiddenSurfaces.bind(this)

        this.scrollTimeout = null

        this.defaultBgColor = null

        this.state = {
            composition: props.composition,
            scrollPos: 0,
            fullscreen: fscreen.fullscreenElement !== null,
            bgImageSource: null,
            canvasWidth: 0,
            canvasHeight: 0,
        }
    }

    componentDidMount() {
        window.addEventListener('scroll', this.onScroll, { passive: true })
        fscreen.addEventListener('fullscreenchange', this.onFullscreenChange)
    }

    componentDidUpdate(prevProps, prevState) {
        const { composition } = this.props

        const renderer = getRenderer()
        const hasComposition = prevState.composition && composition
        const hasNewScene = hasComposition && prevState.composition.scene !== composition.scene
        // Reset if there's a renderer and (we're missing a composition or the scene has changed)
        if (renderer && (!hasComposition || hasNewScene)) {
            this.props.destroyRenderer()
        }

        // wall dimensions are updated from the
        const { wallDimensions } = this.props
        let { canvasWidth } = prevState
        const { wallDimensions: newDimensions } = prevProps
        if (
            canvasWidth === 0 ||
            wallDimensions.width !== newDimensions.width ||
            wallDimensions.height !== newDimensions.height
        ) {
            canvasWidth = this.props.canvasWidth || canvasWidth || getMaxWindowSize()
            const canvasHeight =
                wallDimensions.width > 0 ? canvasWidth * (wallDimensions.height / wallDimensions.width) : 0

            this.setState({
                canvasWidth,
                canvasHeight,
            })
        }
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.onScroll)
        fscreen.removeEventListener('fullscreenchange', this.onFullscreenChange)
    }

    onOrientationChange(orientation) {
        const { setMobileOrientation } = this.props
        setMobileOrientation(orientation)
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const { composition } = nextProps
        return {
            ...prevState,
            composition,
        }
    }

    onScroll() {
        if (this.scrollTimeout !== null) {
            clearTimeout(this.scrollTimeout)
            this.scrollTimeout = null
        }

        this.scrollTimeout = window.setTimeout(this.onScrollFinished, 50)
    }

    onScrollFinished() {
        this.scrollTimeout = null
        this.setState({ scrollPos: window.scrollY })
    }

    onFullscreenChange() {
        const delay = new Promise((resolve) => {
            setTimeout(resolve, 500)
        })
        delay.then(() => this.setState({ fullscreen: fscreen.fullscreenElement !== null }))
    }

    onUpdateCompositionSaveData(saveData, trackingInfoRaw) {
        const trackingInfo = { ...trackingInfoRaw }
        const { composition, onUpdateCompositionSaveData } = this.props
        if (composition) {
            const {
                savedProperties: { erasedSegments },
            } = composition
            trackingInfo.prevErasedSegments = erasedSegments || []
            onUpdateCompositionSaveData(composition.id, saveData, trackingInfo)
        }
    }

    doUpdateHiddenSurfaces(surfs) {
        this.props.updateHiddenSurfaces(this.props.composition.id, surfs)
    }

    render() {
        const {
            composition,
            customContext,
            maxHeight,
            sceneFetchState,
            isLoggedIn,
            debugSettings,
            previewSettings,
            clearSelection,
            hideSurfaces,
            onRendererReady,
            onWallDataReady,
            settings,
            onStackableSurfaceClicked,
            waitingSurfacesInteraction,
            removeRenderItems,
            undoRemoveRenderItems,
            t,
            isMagicWindowLoading,
            onCloseMagicWindow,
            onFirstRender,
            hasRendered,
            wallDimensions,
            showMissingWallsModal,
            closeMissingWallsModal,
            onContextLost,
            onInitializationFailure,
            onContextCreationError,
            onContextRestored,
            setShowDesignSummary,
            orientation,
            maxTextureSize,
            placeStackableOnFloor,
        } = this.props

        const { canvasWidth, canvasHeight } = this.state

        const { isMobile, isKiosk } = this.context

        let maxWidth = null
        const headerOffset = isKiosk ? 0 : 80 // Kiosk has no header
        const useMaxHeight = isMobile ? window.innerHeight : maxHeight - headerOffset
        if (useMaxHeight && wallDimensions.height > 0) {
            const aspect = wallDimensions.width / wallDimensions.height
            maxWidth = useMaxHeight * aspect
        }

        /* eslint-disable i18next/no-literal-string */
        const correctOrientation = isMobile && orientation === supportedOrientation

        const previewContent = (
            <div
                className={classNames('PreviewContainer', { mobile: isMobile, correctOrientation })}
                style={{ maxWidth }}
                ref={(node) => (this.node = node)}
            >
                <Preview
                    hideSurfaces={hideSurfaces}
                    composition={composition}
                    customContext={customContext}
                    canvasWidth={canvasWidth}
                    canvasHeight={canvasHeight}
                    maxTextureSize={maxTextureSize}
                    sceneFetchState={sceneFetchState}
                    isLoggedIn={isLoggedIn}
                    debugSettings={debugSettings}
                    previewSettings={previewSettings}
                    clearSelection={clearSelection}
                    newRenderer={this.props.newRenderer}
                    destroyRenderer={this.props.destroyRenderer}
                    onRendererReady={onRendererReady}
                    onWallDataReady={onWallDataReady}
                    onFirstRender={onFirstRender}
                    settings={settings}
                    onStackableSurfaceClicked={onStackableSurfaceClicked}
                    waitingSurfacesInteraction={waitingSurfacesInteraction}
                    onUpdateCompositionSaveData={this.onUpdateCompositionSaveData}
                    removeRenderItems={removeRenderItems}
                    undoRemoveRenderItems={undoRemoveRenderItems}
                    correctOrientation={correctOrientation}
                    onCloseMagicWindow={onCloseMagicWindow}
                    hasRendered={hasRendered}
                    wallDimensions={wallDimensions}
                    showMissingWallsModal={showMissingWallsModal}
                    closeMissingWallsModal={closeMissingWallsModal}
                    onContextLost={onContextLost}
                    onContextCreationError={onContextCreationError}
                    onContextRestored={onContextRestored}
                    onInitializationFailure={onInitializationFailure}
                    setShowDesignSummary={setShowDesignSummary}
                    placeStackableOnFloor={placeStackableOnFloor}
                    updateHiddenSurfaces={this.doUpdateHiddenSurfaces}
                />
            </div>
        )

        const result = isMobile ? (
            <DeviceOrientation lockOrientation={supportedOrientation} onOrientationChange={this.onOrientationChange}>
                <Orientation orientation={supportedOrientation}>
                    <div style={{ height: window.innerHeight }} className="ContainerBackground">
                        {previewContent}
                    </div>
                </Orientation>
                <Orientation orientation={ORIENTATION_PORTRAIT} alwaysRender={false}>
                    {!isMagicWindowLoading && (
                        <div className="RotatePrompt" ref={(node) => (this.node = node)}>
                            <div className="RotateBackground" />
                            <div className="RotateMessage">
                                <div className="RotateIcon">
                                    <img
                                        src={`${ASSET_BUCKET}/graphics/phone_rotate.svg`}
                                        alt={t('design.alt.no user uploaded rooms')}
                                    />
                                </div>
                                <div>{t('mobile.rotate phone')}</div>
                            </div>
                            <div className="CloseBtnRoot">
                                <button className="CloseButton" onClick={onCloseMagicWindow}>
                                    <Icon code="cross" />
                                </button>
                            </div>
                        </div>
                    )}
                </Orientation>
            </DeviceOrientation>
        ) : (
            previewContent
        )

        return result
    }
}

PreviewContainer.contextType = SugarcubeContext
PreviewContainer.defaultProps = {
    composition: null,
    customContext: CONTEXT_DEFAULT,
    maxHeight: null,
    canvasWidth: null,
    maxTextureSize: null,
    onCloseMagicWindow: null,
}

PreviewContainer.propTypes = {
    composition: compositionType,
    customContext: string,
    maxHeight: number,
    sceneFetchState: string.isRequired,
    isLoggedIn: bool.isRequired,
    debugSettings: debugSettingsType.isRequired,
    previewSettings: previewSettingsType.isRequired,

    clearSelection: func.isRequired,
    newRenderer: func.isRequired,
    destroyRenderer: func.isRequired,
    onRendererReady: func.isRequired,
    onWallDataReady: func.isRequired,
    onFirstRender: func.isRequired,
    settings: previewSettingsType.isRequired,
    canvasWidth: number,
    maxTextureSize: number,
    onStackableSurfaceClicked: func.isRequired,
    waitingSurfacesInteraction: bool.isRequired,
    onUpdateCompositionSaveData: func.isRequired,
    removeRenderItems: func.isRequired,
    undoRemoveRenderItems: func.isRequired,
    t: func.isRequired,
    isMagicWindowLoading: bool.isRequired,
    onCloseMagicWindow: func,
    hasRendered: bool.isRequired,
    placeStackableOnFloor: func.isRequired,
    updateHiddenSurfaces: func.isRequired,
    hideSurfaces: func.isRequired,

    wallDimensions: dimensionsType.isRequired,
    showMissingWallsModal: bool.isRequired,
    closeMissingWallsModal: func.isRequired,
    onContextLost: func.isRequired,
    onContextCreationError: func.isRequired,
    onContextRestored: func.isRequired,
    onInitializationFailure: func.isRequired,
    setShowDesignSummary: func.isRequired,
    setMobileOrientation: func.isRequired,
    orientation: string.isRequired,
}

const mapStateToProps = (state, props) => ({
    composition: props.composition || getSelectedComposition(state),
    sceneFetchState: getFetchState(state),
    isLoggedIn: getIsLoggedIn(state),
    isCurrentDragValid: getIsCurrentDragValid(state),
    debugSettings: getDebugSettings(state),
    previewSettings: getPreviewSettings(state),
    settings: getPreviewSettings(state),
    canvasWidth: getCanvasWidth(state),
    maxTextureSize: getMaxTextureSize(state),
    waitingSurfacesInteraction: getSurfacesInteractive(state),
    isMagicWindowLoading: getIsMagicWindowLoading(state),
    hasRendered: getHasRendered(state),
    wallDimensions: getWallDataDimensions(state),
    showMissingWallsModal: getShowMissingWallsModal(state),
    orientation: getOrientation(state),
})

const mapDispatchToProps = (dispatch) => ({
    hideSurfaces: () => dispatch(hidePlaceableSurfaces()),
    clearSelection: () => dispatch(selectCompositionRenderItem(-1)),
    newRenderer: () => dispatch(newRenderer()),
    destroyRenderer: () => dispatch(destroyRenderer()),
    onRendererReady: (info) => dispatch(rendererReady(info)),
    onWallDataReady: (width, height) => dispatch(wallDataReady(width, height)),
    onFirstRender: () => dispatch(firstRender()),
    onStackableSurfaceClicked: (...args) => dispatch(stackableSurfaceClicked(...args)),
    onUpdateCompositionSaveData: (...args) => dispatch(updateCompositionSaveData(...args)),
    removeRenderItems: (id) => dispatch(removeRenderItemsAction(id)),
    undoRemoveRenderItems: (id) => dispatch(undoRemoveRenderItemsAction(id)),
    closeMissingWallsModal: () => dispatch(showMissingWallsModalAction(false)),
    onContextLost: () => dispatch(webglContextLost()),
    onContextCreationError: () => dispatch(webglContextNotCreated()),
    onContextRestored: () => dispatch(webglContextRestored()),
    onInitializationFailure: (...args) => dispatch(initializationFailure(...args)),
    setMobileOrientation: (orientation) => dispatch(setMobileOrientationAction(orientation)),
    placeStackableOnFloor: () => dispatch(clickPlaceStackableOnFloor()),
    updateHiddenSurfaces: (compositionId, surfs) => dispatch(updateHiddenSurfaces(compositionId, surfs)),
})

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(PreviewContainer))
